import { useState } from "react";
import { MapPin, Clock, Phone, MessageCircle } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { SITE, waLink } from "@/lib/site";
import { api } from "@/lib/api";
import { useFeedback } from "@/components/Feedback";

const INTERESTS = ["Rajwada Bridal", "Temple Nakshi", "Aria Solitaires", "Bangle Atelier", "Heritage Polki", "Lakshmi Coins", "Swarna Nidhi Scheme"];

export const ContactSection = () => {
  const { toast } = useFeedback();
  const [form, setForm] = useState({ name: "", phone: "", date: "", time: "", interest: INTERESTS[0], message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", {
        name: form.name,
        phone: form.phone,
        kind: "appointment",
        message: form.message,
        meta: { date: form.date, time: form.time, interest: form.interest },
      });
      toast("Your private viewing request is with our concierge.");
      setForm({ name: "", phone: "", date: "", time: "", interest: INTERESTS[0], message: "" });
    } catch {
      toast("Something went wrong — please try WhatsApp.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="mx-auto max-w-7xl px-6 py-28 lg:px-12 lg:py-36">
      <SectionHead
        eyebrow="Private Viewing"
        title={<>Reserve the <span className="gold-text italic">bridal lounge.</span></>}
        sub="A quiet hour, a curated tray, and our senior consultant — booked around your occasion."
      />
      <div className="grid gap-12 lg:grid-cols-5">
        <Reveal className="lg:col-span-3">
          <form onSubmit={submit} className="glass-card grid gap-5 rounded-3xl p-8 sm:grid-cols-2 lg:p-10">
            <input
              data-testid="vip-booking-name-input"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Your name"
              className="lux-input"
            />
            <input
              data-testid="vip-booking-phone-input"
              required
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="Phone / WhatsApp"
              className="lux-input"
            />
            <input
              data-testid="vip-booking-date-input"
              type="date"
              required
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="lux-input [color-scheme:dark]"
            />
            <input
              data-testid="vip-booking-time-input"
              type="time"
              required
              value={form.time}
              onChange={(e) => setForm({ ...form, time: e.target.value })}
              className="lux-input [color-scheme:dark]"
            />
            <select
              data-testid="vip-booking-interest-select"
              value={form.interest}
              onChange={(e) => setForm({ ...form, interest: e.target.value })}
              className="lux-input sm:col-span-2"
            >
              {INTERESTS.map((i) => (
                <option key={i} value={i} className="bg-[#101B36]">
                  {i}
                </option>
              ))}
            </select>
            <textarea
              data-testid="vip-booking-message-input"
              rows={3}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Occasion, budget, designs on your mind…"
              className="lux-input resize-none sm:col-span-2"
            />
            <button data-testid="vip-booking-submit-button" type="submit" disabled={busy} className="btn-gold sm:col-span-2 disabled:opacity-60">
              {busy ? "Reserving…" : "Reserve My Viewing"}
            </button>
          </form>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-2">
          <div className="flex h-full flex-col justify-between gap-10 rounded-3xl border border-[#D4AF37]/20 bg-gradient-to-b from-[#13224A] to-[#0B1428] p-8 lg:p-10">
            <div className="space-y-7">
              <img src="/assets/logo-badge.png" alt="Aradhana crest" className="h-14 w-14 rounded-xl" />
              <div className="space-y-5 text-sm font-light text-[#B5B0A8]">
                <p className="flex items-start gap-3">
                  <MapPin size={16} className="mt-0.5 shrink-0 text-[#C59B27]" />
                  <a
                    data-testid="contact-map-link"
                    href={SITE.mapLink}
                    target="_blank"
                    rel="noreferrer"
                    className="transition-colors hover:text-[#E6C265]"
                  >
                    {SITE.address}
                  </a>
                </p>
                <p className="flex items-start gap-3">
                  <Clock size={16} className="mt-0.5 shrink-0 text-[#C59B27]" />
                  {SITE.hours}
                </p>
                <p className="flex items-start gap-3">
                  <Phone size={16} className="mt-0.5 shrink-0 text-[#C59B27]" />
                  <a data-testid="contact-phone-link" href={`tel:${SITE.phoneRaw}`} className="transition-colors hover:text-[#E6C265]">
                    {SITE.phoneDisplay}
                  </a>
                </p>
              </div>
              <p className="border-t border-white/5 pt-6 text-xs font-light leading-relaxed text-[#79756E]">
                Valet parking · Bridal lounge · In-house XRF purity testing · Complimentary refreshments
              </p>
            </div>
            <a
              data-testid="contact-whatsapp-button"
              href={waLink("Namaste Aradhana Jewellers! I'd like to plan a showroom visit.")}
              target="_blank"
              rel="noreferrer"
              className="btn-ghost w-full !py-3"
            >
              <MessageCircle size={15} /> Chat on WhatsApp
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
